<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useParticipants } from '@/composables/useParticipants'
import { useEvents } from '@/composables/useEvents'
import { useEditions } from '@/composables/useEditions'
import type { UpdateRegistrationPayload } from '@/types/participants'
import { RefundStatus } from '@/types/refunds'
import type { OrganizerRefund, RefundFailure } from '@/types/refunds'
import { refundStatusLabelKey } from '@/utils/refundStatus'
import { isReleasedRegistrationStatus } from '@/utils/registrationStatus'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import ConfirmRefundModal from '@/components/organizer/participants/ConfirmRefundModal.vue'
import IssueRefundSlideOver from '@/components/organizer/participants/IssueRefundSlideOver.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'

const { t } = useI18n()
const route = useRoute()
const { registration, loading, error, fetchRegistration, saveRegistration, promoteRegistration } =
  useParticipants()
const { event, fetchEvent } = useEvents()
const { edition, fetchEdition } = useEditions()

const registrationId = Number(route.params['registrationId'])

// Form state
const form = ref<UpdateRegistrationPayload>({
  race_id: 0,
  field_answers: null,
  organizer_remarks: null,
})

const saving = ref(false)
const successMessage = ref('')
// Shown instead of the success message when a cancel took effect but Stripe
// rejected the refund (issue #215). Not auto-dismissed.
const warningMessage = ref('')
const formError = ref('')
const remarksError = ref('')
const confirmModalOpen = ref(false)
const actionLoading = ref(false)
const refundPanelOpen = ref(false)
const refundPrefill = ref<string | null>(null)
// Shown after a custom refund whose provider outcome is not known yet (HTTP 202).
const unconfirmedRefund = ref(false)

const REMARKS_MAX_LENGTH = 2000
const remarksLength = computed(() => (form.value.organizer_remarks ?? '').length)

function isAxiosError(
  e: unknown,
): e is { response: { status: number; data: { message?: string; errors?: Record<string, string[]> } } } {
  return typeof e === 'object' && e !== null && 'response' in e
}

const backUrl = computed(() => {
  if (!registration.value) return '/organizer'
  return `/organizer/events/${registration.value.event_slug}/editions/${registration.value.edition_slug}/participants`
})

const isReleased = computed(() => registration.value ? isReleasedRegistrationStatus(registration.value.status) : false)
const isWaitingList = computed(() => registration.value?.status === 'waiting_list')

// Tier name snapshotted by the backend on the base_price breakdown line.
const registrationFeeLabel = computed<string>(() => {
  const line = registration.value?.price_breakdown.find((item) => item.type === 'base_price')
  return line?.tier_name
    ? t('participants.edit.registration_fee_tier', { tier: line.tier_name })
    : t('participants.edit.registration_fee')
})

const optionLineItems = computed(() =>
  (registration.value?.price_breakdown ?? []).filter(
    (item) => item.type === 'option' || item.type === 'checkbox',
  ),
)

const subtotal = computed(() => {
  const r = registration.value
  if (!r) return 0
  return parseFloat(r.base_price) + parseFloat(r.options_total)
})

const grossRevenue = computed(() => {
  const r = registration.value
  if (!r) return 0
  // Only completed refunds reduce revenue; pending refunds are shown separately.
  return parseFloat(r.amount_paid) - parseFloat(r.refunded_amount)
})

const netRevenue = computed(() => {
  const r = registration.value
  if (!r) return 0
  return grossRevenue.value - parseFloat(r.processing_fee_charged)
})

function refundStatusClass(status: RefundStatus | null): string {
  if (status === RefundStatus.Failed) return 'text-red-400'
  if (status === RefundStatus.Succeeded) return 'text-emerald-400'
  return 'text-slate-300'
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString()
}

function populateForm(): void {
  if (!registration.value) return
  const r = registration.value
  form.value = {
    race_id: r.race.id,
    field_answers: r.field_answers.map((fa) => ({
      wizard_field_id: fa.wizard_field_id,
      value: fa.value,
      selected_options: fa.selected_options,
    })),
    organizer_remarks: r.organizer_remarks,
  }
}

onMounted(async () => {
  await fetchRegistration(registrationId)
  populateForm()
  if (registration.value) {
    void Promise.all([
      fetchEvent(registration.value.event_slug),
      fetchEdition(registration.value.event_slug, registration.value.edition_slug),
    ])
  }
})

async function handleSave(): Promise<void> {
  saving.value = true
  successMessage.value = ''
  formError.value = ''
  remarksError.value = ''
  try {
    await saveRegistration(registrationId, form.value)
    // Re-sync with the server-normalised values (tags stripped, trimmed, empty -> null).
    populateForm()
    successMessage.value = t('participants.edit.save_success')
  } catch (e) {
    if (isAxiosError(e) && e.response.status === 422 && e.response.data.errors?.['organizer_remarks']) {
      remarksError.value = t('participants.edit.internal_remarks_too_long')
    } else {
      formError.value = t('common.error_generic')
    }
  } finally {
    saving.value = false
  }
}

function openCancelModal(): void {
  confirmModalOpen.value = true
}

async function handleConfirmed(refundFailure: RefundFailure | null): Promise<void> {
  await fetchRegistration(registrationId)
  populateForm()
  if (refundFailure === null) {
    warningMessage.value = ''
    successMessage.value = t('participants.edit.cancel_success')
  } else {
    successMessage.value = ''
    warningMessage.value = refundFailure.message || t('participants.refund.not_issued_fallback')
  }
}

function openRefundPanel(prefill: string | null): void {
  refundPrefill.value = prefill
  refundPanelOpen.value = true
}

async function handleRefundIssued(payload: { refund: OrganizerRefund; unconfirmed: boolean }): Promise<void> {
  refundPanelOpen.value = false
  warningMessage.value = ''
  await fetchRegistration(registrationId)
  populateForm()
  if (payload.unconfirmed) {
    successMessage.value = ''
    unconfirmedRefund.value = true
  } else {
    unconfirmedRefund.value = false
    successMessage.value = t('participants.refunds.issued_success', {
      amount: formatCurrency(payload.refund.amount),
    })
  }
}

async function handlePromote(): Promise<void> {
  actionLoading.value = true
  successMessage.value = ''
  formError.value = ''
  try {
    await promoteRegistration(registrationId)
    populateForm()
    successMessage.value = t('participants.edit.promote_success')
  } catch {
    formError.value = t('common.error_generic')
  } finally {
    actionLoading.value = false
  }
}

function formatCurrency(value: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(
    parseFloat(value),
  )
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
    case 'payment_pending':
      return 'bg-amber-500/15 border-amber-500/30 text-amber-400'
    case 'cancelled':
      return 'bg-red-500/15 border-red-500/30 text-red-400'
    case 'payment_expired':
      return 'bg-slate-500/15 border-slate-500/30 text-slate-300'
    case 'waiting_list':
      return 'bg-blue-500/15 border-blue-500/30 text-blue-400'
    default:
      return 'bg-slate-700 border-slate-600 text-slate-400'
  }
}
</script>

<template>
  <OrganizerLayout>

    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Breadcrumb -->
      <nav aria-label="breadcrumb" class="flex items-center gap-2 text-xs text-slate-500 mb-6 flex-wrap">
        <RouterLink to="/organizer/dashboard" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.dashboard') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink to="/organizer/events" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.events') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${registration?.event_slug ?? ''}`" class="hover:text-slate-300 transition-colors">
          <template v-if="event">{{ event.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${registration?.event_slug ?? ''}/editions/${registration?.edition_slug ?? ''}`" class="hover:text-slate-300 transition-colors">
          <template v-if="edition">{{ edition.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="backUrl" class="hover:text-slate-300 transition-colors">
          {{ t('nav.participants') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <span class="text-slate-300">{{ t('participants.edit.title') }}</span>
      </nav>

      <!-- Heading -->
      <div class="mb-8 flex items-center gap-3">
        <h1 class="text-3xl font-bold text-white">
          {{ t('participants.edit.title') }}<template v-if="registration"> - {{ registration.user.first_name }} {{ registration.user.last_name }}</template>
        </h1>
        <span
          v-if="registration"
          :class="statusBadgeClass(registration.status)"
          class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium"
        >
          {{ t(`participants.status.${registration.status}`) }}
        </span>
      </div>

      <!-- Loading state -->
      <AppSpinner v-if="loading" />

      <!-- Error -->
      <div
        v-if="error"
        class="mb-4 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
      >
        {{ t(error) }}
      </div>

      <template v-if="!loading && registration">

        <!-- Success message -->
        <div
          v-if="successMessage"
          class="mb-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-5 py-3 text-sm text-emerald-400"
        >
          {{ successMessage }}
        </div>

        <!-- Refund not issued warning (cancel took effect, Stripe rejected the refund) -->
        <div
          v-if="warningMessage"
          class="mb-4 rounded-xl bg-amber-500/10 border border-amber-500/30 px-5 py-3 text-sm text-amber-300"
          role="status"
          data-testid="refund-not-issued-warning"
        >
          <p class="font-semibold mb-1">{{ t('participants.refund.not_issued_heading') }}</p>
          <p>{{ warningMessage }}</p>
        </div>

        <!-- Custom refund sent but not confirmed yet (HTTP 202) -->
        <div
          v-if="unconfirmedRefund"
          class="mb-4 rounded-xl bg-amber-500/10 border border-amber-500/30 px-5 py-3 text-sm text-amber-300"
          role="status"
          data-testid="refund-unconfirmed-banner"
        >
          <p class="font-semibold mb-1">{{ t('participants.refunds.unconfirmed_heading') }}</p>
          <p>{{ t('participants.refunds.issued_unconfirmed') }}</p>
        </div>

        <!-- Form error -->
        <div
          v-if="formError"
          class="mb-4 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
        >
          {{ formError }}
        </div>

        <form @submit.prevent="handleSave">
          <!-- Section 1: Personal Information (read-only — managed by the participant) -->
          <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 mb-6">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-lg font-bold text-white">{{ t('participants.edit.personal_info') }}</h2>
              <span class="text-xs text-slate-500 italic">{{ t('participants.edit.personal_info_readonly_hint') }}</span>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span class="text-slate-400">{{ t('registration.account.first_name_label') }}</span>
                <p class="text-white font-medium mt-1">{{ registration.user.first_name }}</p>
              </div>
              <div>
                <span class="text-slate-400">{{ t('registration.account.last_name_label') }}</span>
                <p class="text-white font-medium mt-1">{{ registration.user.last_name }}</p>
              </div>
              <div>
                <span class="text-slate-400">{{ t('registration.account.email_label') }}</span>
                <p class="text-white font-medium mt-1">{{ registration.user.email }}</p>
              </div>
              <div>
                <span class="text-slate-400">{{ t('registration.account.gender_label') }}</span>
                <p class="text-white font-medium mt-1">{{ registration.user.gender ? t(`registration.account.gender_${registration.user.gender}`) : '—' }}</p>
              </div>
              <div>
                <span class="text-slate-400">{{ t('registration.account.birth_date_label') }}</span>
                <p class="text-white font-medium mt-1">{{ registration.user.birth_date ?? '—' }}</p>
              </div>
              <div>
                <span class="text-slate-400">{{ t('registration.account.phone_label') }}</span>
                <p class="text-white font-medium mt-1">{{ registration.user.phone ?? '—' }}</p>
              </div>
              <div>
                <span class="text-slate-400">{{ t('registration.account.nationality_label') }}</span>
                <p class="text-white font-medium mt-1">{{ registration.user.nationality ?? '—' }}</p>
              </div>
            </div>
          </div>

          <!-- Section 2: Race Assignment -->
          <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 mb-6">
            <h2 class="text-lg font-bold text-white mb-4">{{ t('participants.edit.race_assignment') }}</h2>
            <select
              v-model="form.race_id"
              required
              class="w-full max-w-xs rounded-lg bg-slate-800 border border-slate-700 text-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option
                v-for="race in registration.available_races"
                :key="race.id"
                :value="race.id"
              >
                {{ race.name }}
              </option>
            </select>
          </div>

          <!-- Section 3: Field Answers -->
          <div
            v-if="registration.field_answers.length > 0"
            class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 mb-6"
          >
            <h2 class="text-lg font-bold text-white mb-4">{{ t('participants.edit.field_answers') }}</h2>
            <div class="space-y-4">
              <div
                v-for="(fa, index) in form.field_answers"
                :key="fa?.wizard_field_id ?? index"
                class="flex flex-col"
              >
                <template v-if="fa">
                  <!-- Label (not rendered for standalone checkbox — label is inline) -->
                  <label
                    v-if="registration.field_answers[index]?.field_type !== 'checkbox'"
                    class="block text-sm font-medium text-slate-300 mb-1"
                  >
                    {{ registration.field_answers[index]?.field_label ?? '' }}
                  </label>

                  <!-- text -->
                  <input
                    v-if="registration.field_answers[index]?.field_type === 'text' || registration.field_answers[index]?.field_type === undefined"
                    v-model="fa.value"
                    type="text"
                    class="w-full rounded-lg bg-slate-800 border border-slate-700 text-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />

                  <!-- textarea -->
                  <textarea
                    v-else-if="registration.field_answers[index]?.field_type === 'textarea'"
                    v-model="fa.value"
                    rows="3"
                    class="w-full rounded-lg bg-slate-800 border border-slate-700 text-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                  />

                  <!-- number -->
                  <input
                    v-else-if="registration.field_answers[index]?.field_type === 'number'"
                    v-model="fa.value"
                    type="number"
                    class="w-full rounded-lg bg-slate-800 border border-slate-700 text-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />

                  <!-- date_picker -->
                  <input
                    v-else-if="registration.field_answers[index]?.field_type === 'date_picker'"
                    v-model="fa.value"
                    type="date"
                    class="w-full rounded-lg bg-slate-800 border border-slate-700 text-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />

                  <!-- checkbox (single boolean) -->
                  <div
                    v-else-if="registration.field_answers[index]?.field_type === 'checkbox'"
                    class="flex items-center gap-2"
                  >
                    <input
                      :id="`field-${fa.wizard_field_id}`"
                      type="checkbox"
                      :checked="fa.value === '1'"
                      class="w-4 h-4 rounded accent-emerald-500 cursor-pointer"
                      @change="fa.value = ($event.target as HTMLInputElement).checked ? '1' : '0'"
                    />
                    <label
                      :for="`field-${fa.wizard_field_id}`"
                      class="text-sm font-medium text-slate-300 cursor-pointer"
                    >
                      {{ registration.field_answers[index]?.field_label ?? '' }}
                    </label>
                  </div>

                  <!-- select / radio_group — rendered as <select> in admin context -->
                  <select
                    v-else-if="registration.field_answers[index]?.field_type === 'select' || registration.field_answers[index]?.field_type === 'radio_group'"
                    :value="fa.value"
                    class="w-full rounded-lg bg-slate-800 border border-slate-700 text-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    @change="(() => {
                      const selectedValue = ($event.target as HTMLSelectElement).value
                      const opts = registration.field_answers[index]?.field_options ?? []
                      const match = opts.find((o) => o.value === selectedValue)
                      fa.value = selectedValue || null
                      fa.selected_options = match ? [match.id] : []
                    })()"
                  >
                    <option value="">-----</option>
                    <option
                      v-for="opt in registration.field_answers[index]?.field_options ?? []"
                      :key="opt.id"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>

                  <!-- checkbox_group — multiple checkboxes -->
                  <div
                    v-else-if="registration.field_answers[index]?.field_type === 'checkbox_group'"
                    class="space-y-2"
                  >
                    <div
                      v-for="opt in registration.field_answers[index]?.field_options ?? []"
                      :key="opt.id"
                      class="flex items-center gap-2"
                    >
                      <input
                        :id="`field-${fa.wizard_field_id}-opt-${opt.id}`"
                        type="checkbox"
                        :checked="(fa.selected_options ?? []).includes(opt.id)"
                        class="w-4 h-4 rounded accent-emerald-500 cursor-pointer"
                        @change="(() => {
                          const checked = ($event.target as HTMLInputElement).checked
                          const current = fa.selected_options ?? []
                          const updated = checked
                            ? [...current, opt.id]
                            : current.filter((id) => id !== opt.id)
                          fa.selected_options = updated
                          const allOpts = registration.field_answers[index]?.field_options ?? []
                          fa.value = allOpts
                            .filter((o) => updated.includes(o.id))
                            .map((o) => o.value)
                            .join(',') || null
                        })()"
                      />
                      <label
                        :for="`field-${fa.wizard_field_id}-opt-${opt.id}`"
                        class="text-sm text-slate-300 cursor-pointer"
                      >
                        {{ opt.label }}
                      </label>
                    </div>
                  </div>

                  <!-- fallback for any unrecognised type -->
                  <input
                    v-else
                    v-model="fa.value"
                    type="text"
                    class="w-full rounded-lg bg-slate-800 border border-slate-700 text-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </template>
              </div>
            </div>
          </div>

          <!-- Section: Internal remarks (organiser-only, issue #222) -->
          <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 mb-6" data-testid="internal-remarks-section">
            <div class="flex items-center justify-between mb-1">
              <h2 class="text-lg font-bold text-white">
                <label for="organizer-remarks">{{ t('participants.edit.internal_remarks') }}</label>
              </h2>
              <span class="text-xs text-slate-500">
                {{ t('participants.edit.internal_remarks_counter', { count: remarksLength, max: REMARKS_MAX_LENGTH }) }}
              </span>
            </div>
            <p id="organizer-remarks-hint" class="text-xs text-slate-500 italic mb-3">
              {{ t('participants.edit.internal_remarks_hint') }}
            </p>
            <textarea
              id="organizer-remarks"
              v-model="form.organizer_remarks"
              rows="4"
              :maxlength="REMARKS_MAX_LENGTH"
              :placeholder="t('participants.edit.internal_remarks_placeholder')"
              aria-describedby="organizer-remarks-hint"
              :aria-invalid="remarksError !== ''"
              class="w-full rounded-lg bg-slate-800 border border-slate-700 text-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-y"
            />
            <p v-if="remarksError" class="mt-1 text-xs text-red-400" role="alert" data-testid="remarks-error">
              {{ remarksError }}
            </p>
          </div>

          <!-- Section 4: Registration Info (read-only) -->
          <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 mb-6 space-y-6">
            <h2 class="text-lg font-bold text-white">{{ t('participants.edit.registration_info') }}</h2>

            <!-- Timeline metadata -->
            <div class="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span class="text-slate-400">{{ t('participants.table.status') }}</span>
                <p class="mt-1">
                  <span
                    :class="statusBadgeClass(registration.status)"
                    class="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium"
                  >
                    {{ t(`participants.status.${registration.status}`) }}
                  </span>
                </p>
              </div>
              <div>
                <span class="text-slate-400">{{ t('participants.table.registered_at') }}</span>
                <p class="text-white font-medium mt-1">{{ formatDate(registration.registered_at) }}</p>
              </div>
              <div v-if="registration.paid_at">
                <span class="text-slate-400">{{ t('participants.edit.paid_at') }}</span>
                <p class="text-white font-medium mt-1">{{ formatDate(registration.paid_at) }}</p>
              </div>
              <div v-if="registration.waiting_list_position !== null">
                <span class="text-slate-400">{{ t('registration.waiting_list.position_label') }}</span>
                <p class="text-white font-medium mt-1">#{{ registration.waiting_list_position }}</p>
              </div>
              <div v-if="registration.promoted_at">
                <span class="text-slate-400">{{ t('participants.edit.promoted_at') }}</span>
                <p class="text-white font-medium mt-1">{{ formatDate(registration.promoted_at) }}</p>
              </div>
              <div v-if="registration.cancelled_at">
                <span class="text-slate-400">{{ t('participants.edit.cancelled_at') }}</span>
                <p class="text-white font-medium mt-1">{{ formatDate(registration.cancelled_at) }}</p>
              </div>
              <div class="col-span-2 md:col-span-3">
                <span class="text-slate-400">{{ t('participants.edit.confirmation_token') }}</span>
                <p class="text-white font-mono text-xs mt-1 break-all">{{ registration.confirmation_token }}</p>
              </div>
            </div>

            <!-- Financial breakdown -->
            <div class="border-t border-slate-700 pt-5">
              <h3 class="text-sm font-semibold text-slate-300 mb-4">{{ t('participants.edit.financial_breakdown') }}</h3>
              <div class="max-w-sm space-y-1.5 text-sm">

                <!-- Registration fee -->
                <div class="flex justify-between">
                  <span class="text-slate-400">{{ registrationFeeLabel }}</span>
                  <span class="text-white">{{ formatCurrency(registration.base_price) }}</span>
                </div>

                <!-- Option line items (indented) -->
                <div
                  v-for="item in optionLineItems"
                  :key="`${item.wizard_field_id}-${item.wizard_field_option_id ?? 'cb'}`"
                  class="flex justify-between pl-4"
                >
                  <span class="text-slate-500 text-xs">
                    {{ item.field_label }}<template v-if="item.option_label">: {{ item.option_label }}</template>
                  </span>
                  <span class="text-slate-400 text-xs">+ {{ formatCurrency(item.amount) }}</span>
                </div>

                <!-- Subtotal -->
                <div class="flex justify-between border-t border-slate-700 pt-1.5">
                  <span class="text-slate-400">{{ t('participants.edit.subtotal') }}</span>
                  <span class="text-white font-medium">{{ formatCurrency(String(subtotal)) }}</span>
                </div>

                <!-- Voucher discount -->
                <div v-if="Number(registration.voucher_discount) > 0" class="flex justify-between">
                  <span class="text-emerald-400 text-xs">
                    {{ t('participants.edit.voucher_discount') }}
                    <span v-if="registration.voucher_code" class="font-mono">({{ registration.voucher_code }})</span>
                  </span>
                  <span class="text-emerald-400 text-xs">− {{ formatCurrency(registration.voucher_discount) }}</span>
                </div>

                <!-- Total paid -->
                <div class="flex justify-between border-t border-slate-700 pt-1.5">
                  <span class="text-white font-semibold">{{ t('participants.edit.total_paid') }}</span>
                  <span class="text-white font-semibold">{{ formatCurrency(registration.amount_paid) }}</span>
                </div>

                <!-- Refund (if applicable) -->
                <div
                  v-if="Number(registration.refunded_amount) > 0"
                  class="flex justify-between"
                  data-testid="price-summary-refunded"
                >
                  <span class="text-red-400 text-xs">− {{ t('participants.edit.refund') }}</span>
                  <span class="text-red-400 text-xs">− {{ formatCurrency(registration.refunded_amount) }}</span>
                </div>

                <!-- Pending refund: informational only, not subtracted -->
                <div
                  v-if="Number(registration.refund_pending_amount) > 0"
                  class="flex justify-between"
                  data-testid="price-summary-refund-pending"
                >
                  <span class="text-slate-500 text-xs">{{ t('participants.edit.refund_pending') }}</span>
                  <span class="text-slate-500 text-xs">{{ formatCurrency(registration.refund_pending_amount) }}</span>
                </div>

                <!-- Gross revenue -->
                <div class="flex justify-between border-t border-slate-700 pt-1.5">
                  <span class="text-slate-400">{{ t('participants.edit.gross_revenue') }}</span>
                  <span class="text-white">{{ formatCurrency(String(grossRevenue)) }}</span>
                </div>

                <!-- Processing fee -->
                <div class="flex justify-between">
                  <span class="text-slate-500 text-xs">− {{ t('participants.edit.processing_fee') }}</span>
                  <span class="text-slate-500 text-xs">− {{ formatCurrency(registration.processing_fee_charged) }}</span>
                </div>

                <!-- Net revenue -->
                <div class="flex justify-between border-t border-slate-700 pt-1.5">
                  <span class="text-slate-300 font-semibold">{{ t('participants.edit.net_revenue') }}</span>
                  <span :class="netRevenue >= 0 ? 'text-emerald-400' : 'text-red-400'" class="font-semibold">
                    {{ formatCurrency(String(netRevenue)) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Payment identifiers (only when a Stripe payment exists) -->
            <template v-if="registration.stripe_payment_status">
              <div class="border-t border-slate-700 pt-5">
                <h3 class="text-sm font-semibold text-slate-300 mb-3">{{ t('participants.edit.payment_details') }}</h3>
                <div class="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span class="text-slate-400">{{ t('participants.edit.stripe_payment_status') }}</span>
                    <p class="text-white font-mono text-xs mt-1">{{ registration.stripe_payment_status }}</p>
                  </div>
                  <div v-if="registration.stripe_payment_method">
                    <span class="text-slate-400">{{ t('participants.edit.stripe_payment_method') }}</span>
                    <p class="text-white font-mono text-xs mt-1">{{ registration.stripe_payment_method }}</p>
                  </div>
                </div>

                <!-- Refunds (every refund of the registration, in creation order) -->
                <div v-if="registration.refunds.length > 0" class="mt-5" data-testid="refunds-list">
                  <h4 class="text-sm font-semibold text-slate-300 mb-3">{{ t('participants.edit.refunds_heading') }}</h4>
                  <ul class="space-y-3">
                    <li
                      v-for="refund in registration.refunds"
                      :key="refund.id"
                      class="rounded-lg border border-slate-700 px-4 py-3 text-sm"
                      data-testid="refund-row"
                    >
                      <div class="flex flex-wrap items-center justify-between gap-2">
                        <span class="text-white font-semibold">{{ formatCurrency(refund.amount) }}</span>
                        <span class="text-xs font-semibold" :class="refundStatusClass(refund.status)">
                          {{ t(refundStatusLabelKey(refund.status)) }}
                        </span>
                        <span class="text-xs text-slate-400">{{ formatDate(refund.refunded_at ?? refund.created_at) }}</span>
                      </div>
                      <p
                        v-if="refund.origin !== null || refund.initiated_by !== null"
                        class="mt-1 text-xs text-slate-400"
                        data-testid="refund-origin"
                      >
                        <template v-if="refund.origin !== null">{{ t(`participants.refunds.origin.${refund.origin}`) }}</template>
                        <template v-if="refund.origin !== null && refund.initiated_by !== null">{{ ' ' }}</template>
                        <template v-if="refund.initiated_by !== null">{{
                          t('participants.refunds.initiated_by', {
                            name: `${refund.initiated_by.first_name} ${refund.initiated_by.last_name}`,
                          })
                        }}</template>
                      </p>
                      <p
                        v-if="refund.reason"
                        class="mt-1 text-xs text-slate-300 whitespace-pre-line"
                        data-testid="refund-reason"
                      >
                        <span class="text-slate-400">{{ t('participants.refunds.internal_reason') }}:</span>
                        {{ refund.reason }}
                      </p>
                      <button
                        v-if="(refund.status === RefundStatus.Failed || refund.status === RefundStatus.Canceled) && registration.refund_blocked_reason === null"
                        type="button"
                        class="mt-2 text-xs font-semibold text-amber-400 hover:text-amber-300"
                        data-testid="refund-again"
                        @click="openRefundPanel(refund.amount)"
                      >
                        {{ t('participants.refunds.refund_again') }}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </template>
          </div>

          <!-- Action bar: equal-width buttons (stacked on mobile, one row from sm up) -->
          <div class="grid gap-3 sm:inline-grid sm:grid-flow-col sm:auto-cols-fr">
            <button
              type="submit"
              :disabled="saving"
              class="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-6 py-2.5 text-sm transition-colors flex items-center justify-center gap-2"
            >
              <svg v-if="saving" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {{ t('participants.edit.save') }}
            </button>

            <!-- Promote button (waiting list only) -->
            <button
              v-if="isWaitingList"
              type="button"
              :disabled="actionLoading"
              class="text-sm font-semibold text-emerald-400 hover:text-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed border border-emerald-500/30 hover:border-emerald-500/60 rounded-lg px-4 py-2.5 transition-colors flex items-center justify-center gap-2"
              @click="handlePromote"
            >
              <svg v-if="actionLoading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {{ t('participants.edit.promote') }}
            </button>

            <button
              v-if="registration.has_online_payment"
              type="button"
              :disabled="registration.refund_blocked_reason !== null"
              class="text-sm font-semibold text-sky-400 hover:text-sky-300 disabled:opacity-50 disabled:cursor-not-allowed border border-sky-500/30 hover:border-sky-500/60 rounded-lg px-4 py-2.5 transition-colors"
              data-testid="refund-button"
              @click="openRefundPanel(null)"
            >
              {{ t('participants.refunds.action') }}
            </button>

            <button
              v-if="!isReleased"
              type="button"
              class="text-sm font-semibold text-amber-400 hover:text-amber-300 border border-amber-500/30 hover:border-amber-500/60 rounded-lg px-4 py-2.5 transition-colors"
              @click="openCancelModal"
            >
              {{ t('participants.edit.cancel_registration') }}
            </button>
          </div>

          <p
            v-if="registration.has_online_payment && registration.refund_blocked_reason !== null"
            class="mt-3 text-xs text-slate-400"
            data-testid="refund-blocked-hint"
          >
            {{ t(`participants.refunds.blocked.${registration.refund_blocked_reason}`) }}
          </p>
        </form>
      </template>

      <!-- Refund slide-over (custom / recovery refund) -->
      <IssueRefundSlideOver
        :open="refundPanelOpen"
        :registration-id="registrationId"
        :prefill-amount="refundPrefill"
        @close="refundPanelOpen = false"
        @issued="handleRefundIssued"
      />

      <!-- Confirm & refund modal (cancel) -->
      <ConfirmRefundModal
        v-model:open="confirmModalOpen"
        :registration-id="registrationId"
        @confirmed="handleConfirmed"
      />
    </main>
  </OrganizerLayout>
</template>
